/**
 * **Numbers, in one place** — HR-3, HR-4 and HR-5 of the house review checklist.
 *
 * ### The abbreviation ladder applies to two places, and only two
 *
 * **KPI tiles and chart axis/data labels** get `K`/`M`/`B`. **Tables, tooltips
 * and exports always show the full number with separators.** That split is the
 * whole rule, and it is not stylistic: a tile is read at a glance and
 * `380,845,231` forces the reader to count digit groups, while a table is read
 * to *check* a figure and an abbreviated cell cannot be checked at all. An
 * export is read by a spreadsheet, which cannot parse `380.8M`.
 *
 * ### Negative counts are real here
 *
 * `SUBSCRIBERS_GAINED` is negative on 860 rows of a 90-day pull — a channel can
 * lose subscribers on a day. So a minus sign is a value this app genuinely
 * renders, and HR-3 asks for negatives as **red parentheses**: `(1,204)`. The
 * parentheses are the accessible half, since colour alone must never carry
 * meaning (VQ-14).
 */

const FULL = new Intl.NumberFormat('en-US')

/**
 * The full number with thousands separators: `380,845,231`.
 *
 * The default. Use it in every table cell, tooltip row and export column.
 */
export function formatNumber(value: number): string {
  return FULL.format(Math.round(value))
}

/**
 * The HR-3 ladder, for **KPI tiles and chart labels only**: `850` · `12.3K` ·
 * `380.8M` · `1.2B`.
 *
 * One decimal at every step above a thousand, so the tiles in a row share a
 * width and the eye can compare them without re-reading. Under a thousand there
 * is nothing to abbreviate and rounding to `0.9K` would lose precision the
 * reader can see is missing.
 */
export function abbreviate(value: number): string {
  const sign = value < 0 ? '-' : ''
  const size = Math.abs(value)
  if (size >= 1_000_000_000) return `${sign}${(size / 1_000_000_000).toFixed(1)}B`
  if (size >= 1_000_000) return `${sign}${(size / 1_000_000).toFixed(1)}M`
  if (size >= 1_000) return `${sign}${(size / 1_000).toFixed(1)}K`
  return `${sign}${Math.round(size)}`
}

/** `12.5%` — one decimal, always, so a column of them lines up (HR-3). */
export function formatPercent(value: number, { fraction = true } = {}): string {
  return `${(fraction ? value * 100 : value).toFixed(1)}%`
}

/**
 * Seconds as a duration with its unit — HR-5: `48 sec` · `9.3 min` · `1.5 hr`.
 *
 * Snap's own view-time fields are milliseconds, so callers divide first. The
 * unit is never dropped: "9.3" against a column called *Avg view time* reads as
 * whichever unit the reader assumes, and the two plausible assumptions here are
 * a factor of 60 apart.
 */
export function formatDuration(seconds: number | null | undefined): string {
  if (seconds === null || seconds === undefined) return '—'
  if (seconds < 60) return `${Math.round(seconds)} sec`
  if (seconds < 3600) return `${(seconds / 60).toFixed(1)} min`
  return `${(seconds / 3600).toFixed(1)} hr`
}

/**
 * A number and whether it should be rendered as a loss.
 *
 * Returned as a pair rather than as markup so the same rule serves a table cell,
 * a tile and a chart label. `text` already carries the parentheses — the caller
 * adds the colour, and must not rely on colour alone.
 */
export function signed(
  value: number,
  format: (value: number) => string = formatNumber,
): { text: string; negative: boolean } {
  const negative = value < 0
  return {
    text: negative ? `(${format(Math.abs(value))})` : format(value),
    negative,
  }
}
